import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useCurrentUser, useLogin, useLogout } from "../hooks";
import { IAuthenticatedUser } from "../types";
import { AuthContext, IAuthContext } from "./AuthContext";

interface IAuthProviderProps {
    children: React.ReactNode;
}

/**
 * AuthProvider
 * Manages the Sanctum SPA session lifecycle:
 *   - Rehydrates session on page load via GET /me (null = no session, user = active)
 *   - Bootstraps CSRF protection on login
 *   - Exposes login/logout and current user through AuthContext
 *
 * Security note: the `token` field from the login response is discarded at the queries
 * layer (see queries.ts). AuthProvider never stores or exposes a Bearer token.
 * The session cookie is the sole SPA credential.
 */
export function AuthProvider({ children }: IAuthProviderProps) {
    const navigate = useNavigate();

    const { mutateAsync: loginMutateAsync } = useLogin();
    const { mutateAsync: logoutMutateAsync } = useLogout();

    const { data: currentUser, isLoading: isSessionCheckLoading } = useCurrentUser();

    // isLoading is true only for the initial fetch (not refetches) — semantically
    // identical to the prior useState/useEffect pattern but without the extra render.
    const isInitialized = !isSessionCheckLoading;

    const login = useCallback(
        async (credentials: Parameters<typeof loginMutateAsync>[0]): Promise<IAuthenticatedUser | undefined> => {
            const user = await loginMutateAsync(credentials);
            navigate("/");
            return user;
        },
        [loginMutateAsync, navigate],
    );

    const logout = useCallback(async (): Promise<void> => {
        await logoutMutateAsync();
        navigate("/auth/sign-in");
    }, [logoutMutateAsync, navigate]);

    const authContextValue = useMemo<IAuthContext>(
        () => ({
            authenticatedUser: currentUser ?? undefined,
            hasAuthenticatedSession: currentUser != null,
            isInitialized,
            login,
            logout,
        }),
        [currentUser, isInitialized, login, logout],
    );

    return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
}
